import React, { useEffect, useState } from "react";
import { ChevronUp, ChevronDown, Filter, Sun, Moon } from 'lucide-react';
import apiClient from '../utils/apiClient';
import { getImageUrl } from '../utils/apiConfig';
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Modal from "../components/Modal";
import PlayerProfileModal from "../components/PlayerProfileModal";
import LeaderboardCard from "../components/LeaderboardCard";
import LeaderboardHero from "../components/LeaderboardHero";
import { Select, TrendIndicator } from "../components/ui";
import ReactSelect from "react-select";
import { useIsMobile } from "../hooks/useIsMobile";
import { useOrientation } from "../hooks/useOrientation";
import { useMediaQuery } from "../hooks/useMediaQuery";
import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import { useAuth } from "../context/UnifiedAuthContext";

/**
 * LeaderboardPage - "Performance Lounge" Season Standings
 *
 * Design Philosophy:
 * - Light mode default (optimized for outdoor/bright light viewing)
 * - Dark mode toggle for evening/indoor use
 * - Data-forward broadcast aesthetic
 * - High contrast for sunlight readability
 * - Premium but approachable feel
 */
const LeaderboardPage = () => {
  const currentYear = new Date().getFullYear().toString();
  const [players, setPlayers] = useState([]);
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: "total_points",
    direction: "desc",
  });
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [onlyPaid, setOnlyPaid] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [selectedSeason, setSelectedSeason] = useState(currentYear);
  const [availableYears, setAvailableYears] = useState([]);
  const { user } = useAuth();
  const [latestUpdateTime, setLatestUpdateTime] = useState(null);
  const isMobile = useIsMobile();
  const isLandscape = useOrientation();
  const isNarrowScreen = useMediaQuery('(max-width: 640px)');

  // Determine view mode
  // Show cards on mobile portrait OR narrow desktop screens
  const showMobileCards = (isMobile && !isLandscape) || isNarrowScreen;
  const showLandscapeTable = isMobile && isLandscape && !isNarrowScreen;

  // Initialize dark mode from localStorage/system preference
  useEffect(() => {
    const stored = localStorage.getItem('theme');
    if (stored) {
      setIsDarkMode(stored === 'dark');
    } else if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      setIsDarkMode(true);
    }
  }, []);

  // Sync dark mode to document
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);

  useEffect(() => {
    tippy('.hpi-tooltip', {
      allowHTML: true,
      theme: 'light-border',
      placement: 'top',
      arrow: true,
      maxWidth: 300,
    });
  }, [players]);

  // Fetch available years
  useEffect(() => {
    const fetchYears = async () => {
      try {
        const response = await apiClient.get('/leaderboard/years');
        const years = response.data.years.map((year) => ({
          value: year.toString(),
          label: year.toString(),
        }));
        setAvailableYears(years);
      } catch (error) {
        // Fallback to current year if fetch fails
        setAvailableYears([{ value: currentYear, label: currentYear }]);
      }
    };
    fetchYears();
  }, [currentYear]);

  const handleSeasonChange = (selectedOption) => {
    setSelectedSeason(selectedOption.value);
  };

  const handleTogglePaidPlayers = () => {
    setOnlyPaid(!onlyPaid);
  };

  const filteredPlayers = onlyPaid
    ? players.filter((player) => player.season_paid)
    : players;

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        const response = await apiClient.get('/leaderboard', {
          params: { limit: 500, year: selectedSeason }
        });
        const payload = response.data || response;
        const leaderboard = payload.leaderboard || [];
        const mappedPlayers = leaderboard.map(player => ({
          ...player,
          id: player.player_id
        }));
        setPlayers(mappedPlayers);
        setLatestUpdateTime(payload.latest_update);
      } catch (error) {
        // Silently handle leaderboard fetch error
      }
    };
    fetchLeaderboard();
  }, [selectedSeason]);

  const handleSort = (key) => {
    let direction = "desc";
    if (key === "name") direction = "asc";
    if (sortConfig.key === key) {
      direction = sortConfig.direction === "asc" ? "desc" : "asc";
    }
    setSortConfig({ key, direction });

    const sortedPlayers = [...players].sort((a, b) => {
      if (key === "name") {
        return direction === "asc"
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      }
      const aValue = typeof a[key] === "number" ? a[key] : parseFloat(a[key]) || 0;
      const bValue = typeof b[key] === "number" ? b[key] : parseFloat(b[key]) || 0;
      if (aValue < bValue) return direction === "asc" ? -1 : 1;
      if (aValue > bValue) return direction === "asc" ? 1 : -1;
      return 0;
    });

    setPlayers(sortedPlayers);
  };

  const closeModal = () => {
    setSelectedPlayer(null);
  };

  const isTop8 = (index) =>
    sortConfig.key === "total_points" &&
    sortConfig.direction === "desc" &&
    index < 8;

  const getPlayerImageUrl = (player) => {
    if (user?.player_id && player.id === user.player_id && user.profilePicture) {
      return getImageUrl(user.profilePicture);
    }
    return getImageUrl(player.image_path);
  };


  const sortOptions = [
    { value: "total_points", label: "Total Points" },
    { value: "rank_change", label: "Trending" },
    { value: "current_quota", label: "Quota" },
    { value: "hpi_score", label: "HPI Score" },
    { value: "money_won", label: "Money Won" },
    { value: "skins", label: "Skins" },
    { value: "wins", label: "Wins" },
    { value: "name", label: "Name" },
  ];

  // Sort indicator component
  const SortIndicator = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return null;
    return sortConfig.direction === "asc"
      ? <ChevronUp className="w-4 h-4 inline ml-1" />
      : <ChevronDown className="w-4 h-4 inline ml-1" />;
  };

  // Rank badge styling
  const getRankBadgeStyle = (rank, isTopEight) => {
    if (rank === 1) return 'bg-gradient-to-br from-gold-400 to-gold-500 text-forest-950 shadow-lg shadow-gold-400/30 ring-2 ring-gold-300';
    if (rank === 2) return 'bg-gradient-to-br from-gray-300 to-gray-400 text-gray-800 shadow-md';
    if (rank === 3) return 'bg-gradient-to-br from-amber-600 to-amber-700 text-white shadow-md';
    if (isTopEight) return 'bg-gold-400/80 text-forest-950';
    return 'bg-gray-200 dark:bg-navy-700 text-gray-700 dark:text-gray-300';
  };

  // Custom styles for season select dropdown
  const seasonSelectStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: "38px",
      fontSize: "14px",
      backgroundColor: isDarkMode ? "#1e3a5f" : "white",
      borderColor: isDarkMode ? "#475569" : "#d1d5db",
      borderRadius: "0.75rem",
      color: isDarkMode ? "#e5e7eb" : "black",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: isDarkMode ? "#e5e7eb" : "black",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: "4px",
      color: isDarkMode ? "#9ca3af" : "#6b7280",
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: "4px 8px",
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: isDarkMode ? "#1e3a5f" : "white",
      border: isDarkMode ? "1px solid #475569" : "1px solid #d1d5db",
      borderRadius: "0.75rem",
    }),
    option: (provided, state) => ({
      ...provided,
      fontSize: "14px",
      backgroundColor: state.isSelected
        ? (isDarkMode ? "#2563eb" : "#cbd5e1")
        : (isDarkMode ? "#1e3a5f" : "white"),
      color: isDarkMode ? "#e5e7eb" : "black",
      "&:hover": {
        backgroundColor: isDarkMode ? "#374151" : "#e2e8f0",
      },
    }),
  };

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-navy-950 flex flex-col transition-colors duration-300">
      <Navbar />

      <main className="flex-grow">
        {/* Hero Header Section */}
        <div className="bg-white dark:bg-navy-900 border-b border-gray-200 dark:border-navy-800">
          <div className="max-w-7xl mx-auto px-4 py-6">
            {/* Title Row */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-6">
                <div>
                  <h1 className="text-3xl md:text-4xl font-display font-bold text-forest-900 dark:text-white tracking-tight">
                    Leaderboard
                  </h1>
                  <p className="text-gray-600 dark:text-navy-400 mt-1">
                    Season Standings
                  </p>
                </div>

                {/* Season Selector */}
                <div className="hidden sm:block">
                  <ReactSelect
                    options={availableYears}
                    value={availableYears.find((option) => option.value === selectedSeason)}
                    onChange={handleSeasonChange}
                    className="w-32"
                    styles={seasonSelectStyles}
                  />
                </div>
              </div>

              <div className="flex items-center gap-4">
                {/* Updated Time */}
                {latestUpdateTime && (
                  <div className="hidden md:flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-forest-500 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-forest-500"></span>
                    </span>
                    Updated {new Date(latestUpdateTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} at {new Date(latestUpdateTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                  </div>
                )}

                {/* Dark Mode Toggle */}
                <button
                  onClick={() => setIsDarkMode(!isDarkMode)}
                  className="
                    flex items-center gap-2 px-4 py-2 rounded-full
                    bg-gray-100 dark:bg-navy-800
                    text-gray-700 dark:text-gray-200
                    hover:bg-gray-200 dark:hover:bg-navy-700
                    transition-colors duration-200
                    border border-gray-200 dark:border-navy-700
                  "
                  aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                  {isDarkMode ? (
                    <>
                      <Sun className="w-5 h-5 text-gold-400" />
                      <span className="text-sm font-medium hidden sm:inline">Light</span>
                    </>
                  ) : (
                    <>
                      <Moon className="w-5 h-5 text-navy-600" />
                      <span className="text-sm font-medium hidden sm:inline">Dark</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Mobile Season Selector */}
            <div className="sm:hidden mb-4">
              <ReactSelect
                options={availableYears}
                value={availableYears.find((option) => option.value === selectedSeason)}
                onChange={handleSeasonChange}
                className="w-full"
                styles={seasonSelectStyles}
              />
            </div>

            {/* Highlights & Trends */}
            <LeaderboardHero
              players={players}
              getPlayerImageUrl={getPlayerImageUrl}
              onPlayerClick={setSelectedPlayer}
            />
          </div>
        </div>

        {/* Content Area */}
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Controls Row */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            {/* Paid Filter Toggle */}
            <div className="flex items-center gap-4">
              <button
                onClick={handleTogglePaidPlayers}
                className={`
                  flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm
                  transition-all duration-200 border
                  ${onlyPaid
                    ? 'bg-forest-600 text-white border-forest-600 shadow-md'
                    : 'bg-white dark:bg-navy-800 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-navy-700 hover:border-forest-300 dark:hover:border-forest-600'
                  }
                `}
              >
                <Filter className="w-4 h-4" />
                {onlyPaid ? "Paid Players" : "All Players"}
              </button>

              {/* Landscape indicator */}
              {showLandscapeTable && (
                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-navy-400">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-forest-500 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-forest-500"></span>
                  </span>
                  Landscape view
                </div>
              )}
            </div>

            {/* Mobile Sort Dropdown */}
            {showMobileCards && (
              <div className="w-full sm:w-64">
                <Select
                  label=""
                  value={sortConfig.key}
                  onChange={(e) => handleSort(e.target.value)}
                  options={sortOptions}
                  className="bg-white dark:bg-navy-800"
                />
              </div>
            )}

            {/* Player Count */}
            <div className="text-sm text-gray-500 dark:text-navy-400 tabular-nums">
              {filteredPlayers.length} player{filteredPlayers.length !== 1 ? 's' : ''}
            </div>
          </div>

          {/* Leaderboard Content */}
          {showMobileCards ? (
            // Mobile Portrait - Card View
            <div className="space-y-3">
              {filteredPlayers.map((player, index) => (
                <LeaderboardCard
                  key={player.id || `player-${index}`}
                  player={player}
                  rank={index + 1}
                  isTop8={isTop8(index)}
                  onPlayerClick={setSelectedPlayer}
                  getPlayerImageUrl={getPlayerImageUrl}
                  sortKey={sortConfig.key}
                />
              ))}
            </div>
          ) : showLandscapeTable ? (
            // Mobile Landscape - Compact Table
            <div className="overflow-x-auto rounded-xl shadow-lg bg-white dark:bg-navy-900 border border-gray-200 dark:border-navy-800">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-forest-900 dark:bg-navy-800 text-white">
                    <th className="p-3 text-center font-semibold cursor-pointer hover:bg-forest-800 dark:hover:bg-navy-700" onClick={() => handleSort("rank_change")}>
                      #<SortIndicator columnKey="rank_change" />
                    </th>
                    <th className="p-3 text-left font-semibold">Player</th>
                    <th className="p-3 text-center font-semibold cursor-pointer hover:bg-forest-800 dark:hover:bg-navy-700" onClick={() => handleSort("current_quota")}>
                      Quota<SortIndicator columnKey="current_quota" />
                    </th>
                    <th className="p-3 text-center font-semibold cursor-pointer hover:bg-forest-800 dark:hover:bg-navy-700" onClick={() => handleSort("money_won")}>
                      $<SortIndicator columnKey="money_won" />
                    </th>
                    <th className="p-3 text-center font-semibold cursor-pointer hover:bg-forest-800 dark:hover:bg-navy-700" onClick={() => handleSort("total_points")}>
                      Pts<SortIndicator columnKey="total_points" />
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-navy-800">
                  {filteredPlayers.map((player, index) => (
                    <tr
                      key={player.id || `player-${index}`}
                      className={`
                        ${isTop8(index)
                          ? 'bg-gradient-to-r from-gold-50 to-white dark:from-gold-900/20 dark:to-navy-900'
                          : index % 2 === 0
                            ? 'bg-white dark:bg-navy-900'
                            : 'bg-gray-50 dark:bg-navy-800/50'
                        }
                        hover:bg-forest-50 dark:hover:bg-navy-700/50
                        transition-colors cursor-pointer
                      `}
                      onClick={() => setSelectedPlayer(player)}
                    >
                      <td className="p-3 text-center">
                        <div className="relative inline-flex items-center justify-center">
                          <span className={`
                            inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold
                            ${getRankBadgeStyle(index + 1, isTop8(index))}
                          `}>
                            {index + 1}
                          </span>
                          {Number(player.rank_change || 0) !== 0 && (
                            <span className={`
                              absolute -bottom-1 -right-1
                              flex items-center justify-center
                              min-w-[14px] h-[14px] px-0.5
                              text-[8px] font-bold rounded-full
                              ${Number(player.rank_change) > 0
                                ? 'bg-emerald-500 text-white'
                                : 'bg-red-500 text-white'
                              }
                            `}>
                              {Number(player.rank_change) > 0 ? '↑' : '↓'}{Math.abs(Number(player.rank_change))}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <img
                            src={getPlayerImageUrl(player)}
                            alt={player.name}
                            className="w-8 h-8 rounded-full object-cover flex-shrink-0 ring-2 ring-white dark:ring-navy-700"
                          />
                          <span className="font-semibold text-gray-900 dark:text-gray-100 truncate max-w-[100px]">
                            {player.name}
                          </span>
                        </div>
                      </td>
                      <td className="p-3 text-center font-semibold tabular-nums text-gray-800 dark:text-gray-200">
                        {player.current_quota}
                      </td>
                      <td className="p-3 text-center font-semibold tabular-nums text-emerald-600 dark:text-emerald-400">
                        ${Number(player.money_won || 0).toFixed(0)}
                      </td>
                      <td className="p-3 text-center">
                        <span className={`
                          inline-flex items-center justify-center px-2.5 py-1 rounded-lg font-bold tabular-nums
                          ${isTop8(index)
                            ? 'bg-gold-400 text-forest-950'
                            : 'bg-forest-100 dark:bg-forest-900/40 text-forest-700 dark:text-forest-400'
                          }
                        `}>
                          {Number(player.total_points || 0).toFixed(0)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            // Desktop - Full Table
            <div className="overflow-x-auto rounded-xl shadow-lg bg-white dark:bg-navy-900 border border-gray-200 dark:border-navy-800">
              <table className="w-full">
                <thead>
                  <tr className="bg-forest-900 dark:bg-navy-800 text-white">
                    <th
                      className="p-4 text-center font-semibold tracking-wide cursor-pointer hover:bg-forest-800 dark:hover:bg-navy-700 transition-colors w-20"
                      onClick={() => handleSort("rank_change")}
                    >
                      Rank<SortIndicator columnKey="rank_change" />
                    </th>
                    <th
                      className="p-4 text-left font-semibold tracking-wide cursor-pointer hover:bg-forest-800 dark:hover:bg-navy-700 transition-colors"
                      onClick={() => handleSort("name")}
                    >
                      Player<SortIndicator columnKey="name" />
                    </th>
                    <th
                      className="p-4 text-center font-semibold tracking-wide cursor-pointer hover:bg-forest-800 dark:hover:bg-navy-700 transition-colors"
                      onClick={() => handleSort("current_quota")}
                    >
                      Quota<SortIndicator columnKey="current_quota" />
                    </th>
                    <th
                      className="p-4 text-center font-semibold tracking-wide cursor-pointer hover:bg-forest-800 dark:hover:bg-navy-700 transition-colors hpi-tooltip"
                      onClick={() => handleSort("hpi_score")}
                      data-tippy-content="Hacker Performance Index"
                    >
                      HPI<SortIndicator columnKey="hpi_score" />
                    </th>
                    <th
                      className="p-4 text-center font-semibold tracking-wide cursor-pointer hover:bg-forest-800 dark:hover:bg-navy-700 transition-colors"
                      onClick={() => handleSort("money_won")}
                    >
                      Money<SortIndicator columnKey="money_won" />
                    </th>
                    <th
                      className="p-4 text-center font-semibold tracking-wide cursor-pointer hover:bg-forest-800 dark:hover:bg-navy-700 transition-colors"
                      onClick={() => handleSort("skins")}
                    >
                      Skins<SortIndicator columnKey="skins" />
                    </th>
                    <th
                      className="p-4 text-center font-semibold tracking-wide cursor-pointer hover:bg-forest-800 dark:hover:bg-navy-700 transition-colors"
                      onClick={() => handleSort("ctps")}
                    >
                      CTPs<SortIndicator columnKey="ctps" />
                    </th>
                    <th
                      className="p-4 text-center font-semibold tracking-wide cursor-pointer hover:bg-forest-800 dark:hover:bg-navy-700 transition-colors"
                      onClick={() => handleSort("wins")}
                    >
                      Wins<SortIndicator columnKey="wins" />
                    </th>
                    <th
                      className="p-4 text-center font-semibold tracking-wide cursor-pointer hover:bg-forest-800 dark:hover:bg-navy-700 transition-colors"
                      onClick={() => handleSort("top_3")}
                    >
                      Top 3<SortIndicator columnKey="top_3" />
                    </th>
                    <th
                      className="p-4 text-center font-semibold tracking-wide cursor-pointer hover:bg-forest-800 dark:hover:bg-navy-700 transition-colors"
                      onClick={() => handleSort("top_10")}
                    >
                      Top 10<SortIndicator columnKey="top_10" />
                    </th>
                    <th
                      className="p-4 text-center font-semibold tracking-wide cursor-pointer hover:bg-forest-800 dark:hover:bg-navy-700 transition-colors"
                      onClick={() => handleSort("events_played")}
                    >
                      Events<SortIndicator columnKey="events_played" />
                    </th>
                    <th
                      className="p-4 text-center font-semibold tracking-wide cursor-pointer hover:bg-forest-800 dark:hover:bg-navy-700 transition-colors"
                      onClick={() => handleSort("total_points")}
                    >
                      Points<SortIndicator columnKey="total_points" />
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-navy-800">
                  {filteredPlayers.map((player, index) => {
                    const rank = index + 1;
                    const topEight = isTop8(index);

                    return (
                      <tr
                        key={player.id || `player-${index}`}
                        className={`
                          ${topEight
                            ? 'bg-gradient-to-r from-gold-50 to-white dark:from-gold-900/20 dark:to-navy-900'
                            : index % 2 === 0
                              ? 'bg-white dark:bg-navy-900'
                              : 'bg-gray-50/80 dark:bg-navy-800/50'
                          }
                          hover:bg-forest-50 dark:hover:bg-navy-700/50
                          transition-colors duration-150 cursor-pointer group
                        `}
                        onClick={() => setSelectedPlayer(player)}
                      >
                        {/* Rank + Trend */}
                        <td className="p-4 text-center">
                          <div className="relative inline-flex items-center justify-center">
                            <div className={`
                              w-10 h-10 rounded-full flex items-center justify-center
                              font-bold text-sm transition-transform group-hover:scale-110
                              ${getRankBadgeStyle(rank, topEight)}
                            `}>
                              {rank}
                            </div>
                            {Number(player.rank_change || 0) !== 0 && (
                              <span className={`
                                absolute -bottom-1 -right-1
                                flex items-center justify-center
                                min-w-[18px] h-[18px] px-1
                                text-[10px] font-bold rounded-full
                                ${Number(player.rank_change) > 0
                                  ? 'bg-emerald-500 text-white'
                                  : 'bg-red-500 text-white'
                                }
                              `}>
                                {Number(player.rank_change) > 0 ? '↑' : '↓'}{Math.abs(Number(player.rank_change))}
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Photo + Name */}
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <img
                              src={getPlayerImageUrl(player)}
                              alt={player.name}
                              className={`
                                w-10 h-10 rounded-full object-cover flex-shrink-0
                                transition-all duration-200 group-hover:scale-105
                                ${topEight
                                  ? 'ring-2 ring-gold-400 ring-offset-2 dark:ring-offset-navy-900'
                                  : 'ring-2 ring-white dark:ring-navy-700 shadow-sm'
                                }
                              `}
                            />
                            <span className={`
                              font-semibold transition-colors whitespace-nowrap
                              ${topEight ? 'text-forest-900 dark:text-gold-300' : 'text-gray-900 dark:text-gray-100'}
                              group-hover:text-forest-700 dark:group-hover:text-forest-400
                            `}>
                              {player.name}
                            </span>
                          </div>
                        </td>

                        {/* Quota */}
                        <td className="p-4 text-center">
                          <span className="font-semibold text-gray-800 dark:text-gray-200 tabular-nums">
                            {player.current_quota}
                          </span>
                        </td>

                        {/* HPI */}
                        <td className="p-4 text-center text-gray-600 dark:text-gray-400 tabular-nums">
                          {Number(player.hpi_score || 0).toFixed(1)}
                        </td>

                        {/* Money */}
                        <td className="p-4 text-center">
                          <span className={`
                            font-semibold tabular-nums
                            ${Number(player.money_won || 0) > 0
                              ? 'text-emerald-600 dark:text-emerald-400'
                              : 'text-gray-400 dark:text-gray-600'
                            }
                          `}>
                            ${Number(player.money_won || 0).toFixed(0)}
                          </span>
                        </td>

                        {/* Skins */}
                        <td className="p-4 text-center text-gray-600 dark:text-gray-400 tabular-nums">
                          {player.skins || 0}
                        </td>

                        {/* CTPs */}
                        <td className="p-4 text-center text-gray-600 dark:text-gray-400 tabular-nums">
                          {player.ctps || 0}
                        </td>

                        {/* Wins */}
                        <td className="p-4 text-center">
                          <span className={`
                            tabular-nums
                            ${(player.wins || 0) > 0
                              ? 'font-semibold text-forest-700 dark:text-forest-400'
                              : 'text-gray-400 dark:text-gray-600'
                            }
                          `}>
                            {player.wins || 0}
                          </span>
                        </td>

                        {/* Top 3 */}
                        <td className="p-4 text-center text-gray-600 dark:text-gray-400 tabular-nums">
                          {player.top_3 || 0}
                        </td>

                        {/* Top 10 */}
                        <td className="p-4 text-center text-gray-600 dark:text-gray-400 tabular-nums">
                          {player.top_10 || 0}
                        </td>

                        {/* Events */}
                        <td className="p-4 text-center text-gray-600 dark:text-gray-400 tabular-nums">
                          {player.events_played || 0}
                        </td>

                        {/* Points - Hero Column */}
                        <td className="p-4 text-center">
                          <span className={`
                            inline-flex items-center justify-center
                            min-w-[3.5rem] px-3 py-2 rounded-lg
                            font-bold text-lg tabular-nums
                            ${topEight
                              ? 'bg-gold-400 text-forest-950'
                              : 'bg-forest-100 dark:bg-forest-900/40 text-forest-700 dark:text-forest-400'
                            }
                          `}>
                            {Number(player.total_points || 0).toFixed(0)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Player Profile Modal */}
      {selectedPlayer && (
        <PlayerProfileModal
          player={selectedPlayer}
          onClose={closeModal}
        />
      )}

      {/* Enlarged Image Modal */}
      {enlargedImage && (
        <Modal onClose={() => setEnlargedImage(null)}>
          <div className="flex justify-center items-center p-6">
            <img
              src={enlargedImage}
              alt="Enlarged"
              className="max-w-full max-h-[90vh] rounded-lg shadow-lg"
            />
          </div>
        </Modal>
      )}

      <Footer />
    </div>
  );
};

export default LeaderboardPage;
